import {
  withTextComponent,
  withTemplate,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';
import { withFormValidation } from '@updater-components/form-element';
import '@updater-components/fa-icon';
import styles from './index.css';

const html = `
  <select class="upd-select__select"></select>
  <upd-fa-icon class="upd-select__select-icon" name="chevron-down"></upd-fa-icon>
`;

const OBSERVED_ATTRIBUTES = ['name', 'placeholder', 'required', 'value'];

const IS_IE_OR_EDGE = document.documentMode || /Edge/.test(navigator.userAgent);

export default class Select extends withFormValidation(
  withTemplate(html)(withTextComponent())
) {
  static props = ['options'];

  static observedAttributes = OBSERVED_ATTRIBUTES;

  static reflectedAttributes = OBSERVED_ATTRIBUTES;

  static attributeTypes = { required: 'boolean' };

  _handleChangeOrInput = (event) => {
    // Stop the internal `input`'s event at this level to prevent duplicate events.
    event.stopPropagation();

    const {
      target: { value },
      type,
    } = event;

    if (this.value === value) {
      // There may be multiple options with the same value, but only the first
      // of them (in DOM tree order) can be selected.
      // https://www.w3.org/TR/html5/sec-forms.html#dom-htmlselectelement-value
      // Force the internal `input`'s selection back to the first option with a
      // matching `value`. E.g. this handles the case where selecting the empty
      // option results in the placeholder being selected (both have empty
      // string as the option value).
      this._input.value = value;
    }

    this.value = value;

    if (IS_IE_OR_EDGE) {
      // this resolves an issue where an invalid state
      // takes two selections to clear.
      setTimeout(() => {
        this.checkValidity();
      });
    } else {
      this.checkValidity();
    }

    // Re-dispatch interaction events with `composed` and `detail`.
    this.dispatchEvent(
      new CustomEvent(
        type,
        Object.assign({}, event, {
          bubbles: true,
          composed: true,
          detail: value,
        })
      )
    );
  };

  connected() {
    this.value = this.value || '';

    const template = document.createElement('template');
    template.innerHTML = `<style>${styles}</style>`;
    const style = template.content.cloneNode(true);

    this.appendChild(style);
    this.appendChild(this._template);

    this._input = this.querySelector('select');
    this._input.addEventListener('change', this._handleChangeOrInput);
    this._input.addEventListener('input', this._handleChangeOrInput);
  }

  disconnected() {
    this._input.removeEventListener('change', this._handleChangeOrInput);
    this._input.removeEventListener('input', this._handleChangeOrInput);
  }

  updated(
    { options, placeholder, required, value },
    { options: oldOptions, placeholder: oldPlaceholder, required: oldRequired }
  ) {
    if (
      options !== oldOptions ||
      placeholder !== oldPlaceholder ||
      required !== oldRequired
    ) {
      this._input.innerHTML = ''; // Clear existing options.

      if (placeholder) {
        this._input.appendChild(createPlaceholderOption(placeholder));
      }

      if (!required) {
        // Allow user to remove a selection if the field isn't required.
        this._input.appendChild(createOption(''));
      }

      (options || []).forEach((value) => {
        this._input.appendChild(createOption(value));
      });
    }

    // There may be multiple options with the same value, but only the first of
    // them (in DOM tree order) can be selected.
    // https://www.w3.org/TR/html5/sec-forms.html#dom-htmlselectelement-value
    const children = Array.from(this._input.children);
    const selectedIndex = children.findIndex((child) => child.value === value);
    children.forEach((child, index) => {
      applyAsAttributes(child, { selected: index === selectedIndex });
    });

    if (value) {
      this.classList.remove('upd-select-empty');
    } else {
      this.classList.add('upd-select-empty');
    }

    applyAsAttributes(this._input, { required, value });
  }
}

/**
 * Creates an `<option>` element from a string or object. In the former case,
 * the value is the text content of the option. In the latter case, the object's
 * keys are mapped to the option's attributes.
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/option#Attributes
 *
 * @param {String|Object} source
 * @return {HTMLOptionElement}
 */
function createOption(source) {
  const el = document.createElement('option');

  if (typeof source === 'object') {
    const {
      disabled = false,
      label = '',
      selected = false,
      value = '',
    } = source;

    applyAsAttributes(el, { disabled, label, selected, value });

    // Should be redundant since we're setting the `label` attribute,
    // but FF doesn't yet support it:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=40545
    el.textContent = label;
  } else {
    el.textContent = source;
  }

  return el;
}

// https://stackoverflow.com/a/5859221/89594
function createPlaceholderOption(label) {
  return createOption({
    disabled: true,
    label,
  });
}

customElements.define('upd-select', Select);
